import React, { useState } from 'react';
import { Lock, Loader2, AlertCircle } from 'lucide-react';

/**
 * Modal de sesión expirada (Feb 2026).
 *
 * Se muestra únicamente cuando el refresh automático ha fallado. NO redirige
 * ni recarga la página — el usuario mantiene su URL y sus datos no guardados
 * en memoria. Al re-autenticarse correctamente, el modal se cierra y el
 * usuario puede continuar donde estaba (típicamente pulsar "Guardar" otra vez).
 */
const SessionExpiredModal = ({ currentEmail = '', onResolve }) => {
  const [email, setEmail] = useState(currentEmail);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Introduce email y contraseña.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onResolve(email.trim(), password);
      // El propio modal se desmonta cuando sessionExpired pasa a false.
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo iniciar sesión';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(2, 6, 23, 0.78)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      data-testid="session-expired-modal"
    >
      <div
        className="w-full max-w-md rounded-xl shadow-2xl overflow-hidden"
        style={{ background: 'var(--color-bg-secondary, #0f172a)', border: '1px solid var(--color-border, #334155)' }}
      >
        <div className="p-6 border-b" style={{ borderColor: 'var(--color-border, #334155)' }}>
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(239, 68, 68, 0.15)' }}
            >
              <Lock className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 id="session-expired-title" className="text-lg font-semibold" style={{ color: 'var(--color-text-primary, #f1f5f9)' }}>
                Tu sesión ha caducado
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted, #94a3b8)' }}>
                Hemos intentado renovarla automáticamente, pero no ha sido posible.
                Vuelve a iniciar sesión para continuar — tus cambios en pantalla no se perderán.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="session-expired-form">
          <div>
            <label htmlFor="session-email" className="text-xs uppercase tracking-wide font-medium" style={{ color: 'var(--color-text-muted, #94a3b8)' }}>
              Email
            </label>
            <input
              id="session-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus={!currentEmail}
              required
              className="w-full mt-1 px-3 py-2 rounded border bg-slate-900/40 text-slate-100 placeholder-slate-500"
              style={{ borderColor: 'var(--color-border, #334155)' }}
              data-testid="session-expired-email"
            />
          </div>
          <div>
            <label htmlFor="session-password" className="text-xs uppercase tracking-wide font-medium" style={{ color: 'var(--color-text-muted, #94a3b8)' }}>
              Contraseña
            </label>
            <input
              id="session-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus={!!currentEmail}
              required
              className="w-full mt-1 px-3 py-2 rounded border bg-slate-900/40 text-slate-100 placeholder-slate-500"
              style={{ borderColor: 'var(--color-border, #334155)' }}
              data-testid="session-expired-password"
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded text-sm"
              style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.4)' }}
              data-testid="session-expired-error"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded font-medium transition-colors disabled:opacity-60"
            style={{ background: 'var(--color-blue-accent, #3b82f6)', color: 'white' }}
            data-testid="session-expired-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Iniciando sesión…' : 'Volver a iniciar sesión'}
          </button>

          <p className="text-xs text-center" style={{ color: 'var(--color-text-muted, #94a3b8)' }}>
            Permaneces en la misma página. Al validar, podrás continuar donde lo dejaste.
          </p>
        </form>
      </div>
    </div>
  );
};

export default SessionExpiredModal;
